<script setup>
import { ref, computed, watch } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

const isLoading = ref(false);
const reportData = ref(null);

const now = new Date();
const monthOptions = Array.from({ length: 12 }, (_, i) => {
  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
  return {
    label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
    month: d.getMonth() + 1,
    year: d.getFullYear(),
  };
});

const selectedMonthIndex = ref(0);
const selectedMonth = computed(() => monthOptions[selectedMonthIndex.value]);

const summary = computed(() => reportData.value?.summary || {});
const activitySummary = computed(() => reportData.value?.activity || {});
const alertsByType = computed(() => reportData.value?.alerts_by_type || []);
const leadershipPerformance = computed(
  () => reportData.value?.leadership_performance || []
);

const summaryCards = computed(() => [
  {
    label: 'Total Posts',
    value: summary.value.total_posts ?? '-',
    icon: 'document',
    color: 'bg-n-blue-3 text-n-blue-11',
  },
  {
    label: 'Total Alertas',
    value: summary.value.total_alerts ?? '-',
    icon: 'alert',
    color: 'bg-n-ruby-3 text-n-ruby-11',
  },
  {
    label: 'Sentimento Predominante',
    value: summary.value.predominant_sentiment ?? '-',
    icon: 'emoji',
    color: 'bg-n-jade-3 text-n-jade-11',
  },
  {
    label: 'Perfis Monitorados',
    value: summary.value.monitored_profiles ?? '-',
    icon: 'people',
    color: 'bg-n-violet-3 text-n-violet-11',
  },
]);

function sentimentLabel(sentiment) {
  const labels = {
    positive: 'Positivo',
    negative: 'Negativo',
    neutral: 'Neutro',
  };
  return labels[sentiment] || sentiment;
}

function sentimentBadgeClass(sentiment) {
  const classes = {
    positive: 'bg-n-jade-3 text-n-jade-11',
    negative: 'bg-n-ruby-3 text-n-ruby-11',
    neutral: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[sentiment] || 'bg-n-slate-3 text-n-slate-11';
}

async function fetchReport() {
  if (!props.selectedInbox?.id) return;
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getMonthlyReport({
      inboxId: props.selectedInbox.id,
      month: selectedMonth.value.month,
      year: selectedMonth.value.year,
    });
    reportData.value = response.data;
  } catch {
    reportData.value = null;
  } finally {
    isLoading.value = false;
  }
}

function exportPdf() {
  // placeholder for PDF export
}

watch(
  [() => props.selectedInbox?.id, selectedMonthIndex],
  () => {
    if (props.selectedInbox?.id) {
      fetchReport();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Header with month selector and export -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <label class="text-sm font-medium text-n-slate-11">
          Per&iacute;odo:
        </label>
        <select
          v-model="selectedMonthIndex"
          class="rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12"
        >
          <option
            v-for="(opt, idx) in monthOptions"
            :key="idx"
            :value="idx"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>
      <button
        class="flex items-center gap-2 rounded-lg bg-n-blue-9 px-4 py-2 text-sm font-medium text-white hover:bg-n-blue-10"
        @click="exportPdf"
      >
        <fluent-icon icon="arrow-download" size="16" />
        Exportar PDF
      </button>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-16">
      <Spinner />
    </div>

    <template v-else>
      <!-- Summary Cards -->
      <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div
          v-for="card in summaryCards"
          :key="card.label"
          class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-black2 p-4"
        >
          <div
            class="flex h-10 w-10 items-center justify-center rounded-full"
            :class="card.color"
          >
            <fluent-icon :icon="card.icon" size="20" />
          </div>
          <div>
            <p class="text-xs text-n-slate-10">{{ card.label }}</p>
            <p class="text-lg font-bold text-n-slate-12">{{ card.value }}</p>
          </div>
        </div>
      </div>

      <!-- Resumo de Atividade -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4">
        <h3 class="mb-3 text-base font-semibold text-n-slate-12">
          Resumo de Atividade
        </h3>
        <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div>
            <p class="text-xs text-n-slate-10">Posts analisados</p>
            <p class="text-lg font-semibold text-n-slate-12">
              {{ activitySummary.posts_analyzed ?? '-' }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-10">Coment&aacute;rios coletados</p>
            <p class="text-lg font-semibold text-n-slate-12">
              {{ activitySummary.comments_collected ?? '-' }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-10">Alertas gerados</p>
            <p class="text-lg font-semibold text-n-slate-12">
              {{ activitySummary.alerts_generated ?? '-' }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-10">A&ccedil;&otilde;es realizadas</p>
            <p class="text-lg font-semibold text-n-slate-12">
              {{ activitySummary.actions_taken ?? '-' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Alertas por Tipo -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4">
        <h3 class="mb-3 text-base font-semibold text-n-slate-12">
          Alertas por Tipo
        </h3>
        <div
          v-if="alertsByType.length === 0"
          class="py-6 text-center text-sm text-n-slate-10"
        >
          Nenhum alerta no per&iacute;odo
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="item in alertsByType"
            :key="item.type"
            class="flex items-center justify-between rounded-lg bg-n-alpha-black2 px-3 py-2"
          >
            <span class="text-sm text-n-slate-11">{{ item.type }}</span>
            <span
              class="rounded-full px-2 py-0.5 text-xs font-medium"
              :class="sentimentBadgeClass(item.sentiment)"
            >
              {{ item.count }}
            </span>
          </div>
        </div>
      </div>

      <!-- Performance das Lideranças -->
      <div class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4">
        <h3 class="mb-3 text-base font-semibold text-n-slate-12">
          Performance das Lideran&ccedil;as
        </h3>
        <div
          v-if="leadershipPerformance.length === 0"
          class="py-6 text-center text-sm text-n-slate-10"
        >
          Nenhuma lideran&ccedil;a monitorada
        </div>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr
                class="border-b border-n-weak text-left text-xs text-n-slate-10"
              >
                <th class="px-3 py-2">Perfil</th>
                <th class="px-3 py-2 text-right">Posts</th>
                <th class="px-3 py-2 text-right">Alertas</th>
                <th class="px-3 py-2 text-center">Sentimento</th>
                <th class="px-3 py-2 text-right">Engajamento</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="leader in leadershipPerformance"
                :key="leader.username"
                class="border-b border-n-weak last:border-0"
              >
                <td class="px-3 py-2 font-medium text-n-slate-12">
                  @{{ leader.username }}
                </td>
                <td class="px-3 py-2 text-right text-n-slate-11">
                  {{ leader.posts_count }}
                </td>
                <td class="px-3 py-2 text-right text-n-slate-11">
                  {{ leader.alerts_count }}
                </td>
                <td class="px-3 py-2 text-center">
                  <span
                    class="rounded-full px-2 py-0.5 text-xs font-medium"
                    :class="sentimentBadgeClass(leader.predominant_sentiment)"
                  >
                    {{ sentimentLabel(leader.predominant_sentiment) }}
                  </span>
                </td>
                <td class="px-3 py-2 text-right text-n-slate-11">
                  {{ leader.engagement_score }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>
